import { VmType } from '~/types'
import * as EvmAddress from '~/utils/address/evm'
import * as SolanaAddress from '~/utils/address/solana'
import { Formatted, AddressType, Address } from '~/utils/address/types'

const formatCache = new Map<string, Formatted<Address>>()
const validationCache = new Map<string, boolean>()

export type { Address as Type }

export function vmTypes(address: Address): VmType[] {
  const vms: VmType[] = []

  if (EvmAddress.validate(address)) {
    vms.push('evm')
  }

  if (SolanaAddress.validate(address)) {
    vms.push('svm')
  }

  return vms
}

export function equal(
  str1: string | undefined | null,
  str2: string | undefined | null,
  type?: AddressType,
): boolean {
  if (!str1 || !str2) return false
  return safeFormat(str1, type) === safeFormat(str2, type)
}

export function supportedTypes(vmType: VmType): AddressType[] {
  switch (vmType) {
    case 'evm':
      return ['evm']
    case 'svm':
      return ['solana']
  }
}

/**
 * Checks that two addresses are theoretically compatible (can be used on the same VM)
 */
export function compatible(addr1: Address, addr2: Address): boolean {
  const vms1 = vmTypes(addr1)
  const vms2 = vmTypes(addr2)
  return vms1.some((vm1) => vms2.includes(vm1))
}

export function parse(str: string): AddressType {
  if (validate(str, 'evm')) {
    return 'evm'
  }

  if (validate(str, 'solana')) {
    return 'solana'
  }

  throw new Error(`Invalid address: ${str}`)
}

export function validate(str: string, type: 'evm'): str is EvmAddress.Type
export function validate(str: string, type: 'solana'): str is SolanaAddress.Type
export function validate(
  str: string,
  type?: AddressType | AddressType[],
): str is Address
export function validate(
  str: string,
  type?: AddressType | AddressType[],
): str is Address {
  if (Array.isArray(type)) {
    return type.some((t) => validate(str, t))
  }

  const cacheKey = `${type ?? 'any'}:${str}`
  const cachedValidation = validationCache.get(cacheKey)
  if (cachedValidation !== undefined) {
    return cachedValidation
  }

  if (type === 'evm' || !type) {
    const valid = EvmAddress.validate(str)
    if (valid) {
      validationCache.set(cacheKey, true)
      return valid
    }
  }

  if (type === 'solana' || !type) {
    const valid = SolanaAddress.validate(str)
    if (valid) {
      validationCache.set(cacheKey, true)
      return valid
    }
  }

  validationCache.set(cacheKey, false)
  return false
}

export function safeFormat(
  str: string | undefined | null,
  type: 'evm',
): Formatted<EvmAddress.Type> | undefined
export function safeFormat(
  str: string | undefined | null,
  type: 'solana',
): Formatted<SolanaAddress.Type> | undefined
export function safeFormat(
  str: string | undefined | null,
  type?: AddressType,
): Formatted<Address> | undefined
export function safeFormat(
  str: string | undefined | null,
  type?: AddressType,
): Formatted<Address> | undefined {
  return str ? format(str, type) : undefined
}

export function format(str: string, type: 'evm'): Formatted<EvmAddress.Type>
export function format(
  str: string,
  type: 'solana',
): Formatted<SolanaAddress.Type>
export function format(str: string, type?: AddressType): Formatted<Address>
export function format(str: string, type?: AddressType): Formatted<Address> {
  const cacheKey = `${type ?? 'any'}:${str}`
  const cachedAddress = formatCache.get(cacheKey)
  if (cachedAddress) {
    return cachedAddress
  }

  if (type === 'evm' || !type) {
    try {
      const address = EvmAddress.format(str)
      formatCache.set(cacheKey, address)
      return address
    } catch {
      // Ignore
    }
  }

  if (type === 'solana' || !type) {
    try {
      const address = SolanaAddress.format(str)
      formatCache.set(cacheKey, address)
      return address
    } catch {
      // Ignore
    }
  }

  throw new Error(
    type ? `Invalid ${type} address: ${str}` : `Invalid address: ${str}`,
  )
}
